<template>
  <AppShell>
    <!-- Greeting -->
    <div class="flex items-end justify-between gap-4 mb-5">
      <div>
        <p class="eyebrow">Welcome back</p>
        <h1 class="mt-1.5 text-2xl sm:text-3xl font-extrabold text-white">
          Hello, <span class="gradient-text">{{ username }}</span>
        </h1>
      </div>
      <a href="/modules" class="hidden sm:inline-flex btn-secondary">
        <i class="fas fa-compass"></i> Explore
      </a>
    </div>

    <!-- ===== BENTO ROW 1: continue-learning + rank ===== -->
    <div class="grid grid-cols-1 lg:grid-cols-3 gap-5 mb-5">
      <!-- Continue learning hero -->
      <div class="lg:col-span-2 card overflow-hidden relative group min-h-[200px] flex">
        <div v-if="continueModule" class="absolute inset-0">
          <img :src="continueModule.imageUrl" :alt="continueModule.title" class="w-full h-full object-cover opacity-25 group-hover:opacity-30 transition-opacity" @error="onImgError" />
          <div class="absolute inset-0 bg-gradient-to-r from-surface via-surface/90 to-surface/40"></div>
        </div>
        <div v-else class="absolute inset-0 bg-hero-glow"></div>

        <div class="relative p-6 sm:p-8 flex flex-col justify-center w-full">
          <template v-if="continueModule">
            <span class="chip self-start mb-3 border-brand/40 text-brand bg-brand/10">
              <i class="fas fa-play"></i> Continue learning
            </span>
            <h2 class="text-xl sm:text-2xl font-extrabold text-white mb-2 line-clamp-1">{{ continueModule.title }}</h2>
            <p class="text-ink-muted text-sm mb-5 line-clamp-2 max-w-xl">{{ continueModule.description }}</p>
            <a :href="`/modules/${continueModule.id}`" class="btn-primary self-start px-5 py-2.5">
              Resume <i class="fas fa-arrow-right text-xs"></i>
            </a>
          </template>
          <template v-else>
            <span class="chip self-start mb-3 border-brand/40 text-brand bg-brand/10">
              <i class="fas fa-rocket"></i> Get started
            </span>
            <h2 class="text-xl sm:text-2xl font-extrabold text-white mb-2">Start your first module</h2>
            <p class="text-ink-muted text-sm mb-5 max-w-xl">Pick a learning path and begin owning machines today.</p>
            <a href="/modules" class="btn-primary self-start px-5 py-2.5">
              Browse modules <i class="fas fa-arrow-right text-xs"></i>
            </a>
          </template>
        </div>
      </div>

      <!-- Rank progress (clickable) -->
      <div class="card card-hover p-6 cursor-pointer flex flex-col items-center text-center" @click="showRankModal = true">
        <img :src="rankBadgeUrl" alt="Rank Badge" class="h-20 w-20 mb-3 drop-shadow-[0_0_20px_rgba(255,49,49,.3)]" />
        <div class="flex items-center gap-2 mb-1">
          <h2 class="text-lg font-bold text-white">{{ rank }}</h2>
          <i class="fas fa-circle-info text-ink-faint text-xs"></i>
        </div>
        <p class="text-ink-muted text-sm mb-3">{{ points }} Points</p>
        <div class="w-full h-2 rounded-full bg-surface-lighter overflow-hidden">
          <div class="h-full bg-brand-gradient rounded-full transition-all duration-700" :style="{ width: progressBarWidth }"></div>
        </div>
        <p class="text-xs text-ink-muted mt-2">
          Next: <span class="text-white font-semibold">{{ nextRank }}</span> · {{ nextRankPoints }} pts
        </p>
      </div>
    </div>

    <!-- ===== BENTO ROW 2: stats strip ===== -->
    <div class="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
      <div class="card p-5">
        <div class="flex items-center justify-between">
          <p class="text-xs uppercase tracking-wider text-ink-faint">Rank</p>
          <i class="fas fa-shield-halved text-brand"></i>
        </div>
        <p class="mt-2 text-xl font-extrabold text-white truncate">{{ rank }}</p>
      </div>
      <div class="card p-5">
        <div class="flex items-center justify-between">
          <p class="text-xs uppercase tracking-wider text-ink-faint">Points</p>
          <i class="fas fa-fire text-brand"></i>
        </div>
        <p class="mt-2 text-xl font-extrabold gradient-text">{{ points }}</p>
      </div>
      <div class="card p-5">
        <div class="flex items-center justify-between">
          <p class="text-xs uppercase tracking-wider text-ink-faint">In Progress</p>
          <i class="fas fa-spinner text-info"></i>
        </div>
        <p class="mt-2 text-xl font-extrabold text-white">{{ inProgressCount }}</p>
      </div>
      <div class="card p-5">
        <div class="flex items-center justify-between">
          <p class="text-xs uppercase tracking-wider text-ink-faint">Completed</p>
          <i class="fas fa-circle-check text-success"></i>
        </div>
        <p class="mt-2 text-xl font-extrabold text-white">{{ completedCount }}</p>
      </div>
    </div>

    <!-- Subscription upsell banner (only for free users) -->
    <a v-if="!isSubscribed" href="/subscribe" class="card card-hover p-5 mb-8 flex items-center justify-between gap-4 group">
      <div class="flex items-center gap-4">
        <div class="h-11 w-11 grid place-items-center rounded-xl bg-yellow-500/15 text-yellow-400 text-lg shrink-0">
          <i class="fas fa-crown"></i>
        </div>
        <div>
          <p class="font-bold text-white">Upgrade to Premium</p>
          <p class="text-sm text-ink-muted">Unlock exclusive modules, 500 AI messages/week & 50 machines/week.</p>
        </div>
      </div>
      <span class="btn-gold shrink-0 hidden sm:inline-flex">Upgrade <i class="fas fa-arrow-right text-xs"></i></span>
    </a>

    <!-- Tabs -->
    <div class="flex items-center justify-between mb-6 border-b border-line">
      <div class="flex gap-1">
        <button
          v-for="tab in tabs"
          :key="tab.id"
          @click="setActiveTab(tab.id)"
          :class="[
            'px-4 py-3 text-sm font-semibold border-b-2 -mb-px transition-colors',
            tab.id === activeTab ? 'text-brand border-brand' : 'text-ink-muted border-transparent hover:text-white',
          ]"
        >
          {{ tab.label }}
        </button>
      </div>
      <a href="/modules" class="hidden sm:inline-flex text-sm font-semibold text-brand hover:text-brand-light">
        View all <i class="fas fa-arrow-right text-xs"></i>
      </a>
    </div>

    <!-- Courses grid -->
    <div v-if="filteredModules.length" class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
      <a
        v-for="(course, index) in filteredModules"
        :key="index"
        :href="`/modules/${course.id}`"
        class="card card-hover overflow-hidden group flex flex-col"
      >
        <div class="relative h-40 overflow-hidden bg-surface-light">
          <img :src="course.imageUrl" :alt="course.title" class="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" @error="onImgError" />
          <div class="absolute inset-0 bg-gradient-to-t from-surface via-transparent to-transparent"></div>
          <span v-if="course.isExclusive" class="badge absolute top-3 right-3 bg-yellow-500 text-black"><i class="fas fa-crown"></i> Premium</span>
          <span v-if="course.isCompleted" class="badge absolute top-3 left-3 bg-success text-black"><i class="fas fa-check"></i> Completed</span>
          <span v-else-if="course.isEnrolled" class="badge absolute top-3 left-3 bg-info text-black"><i class="fas fa-bookmark"></i> Enrolled</span>
        </div>
        <div class="p-5 flex-1 flex flex-col">
          <h2 class="text-lg font-bold text-white mb-1.5 line-clamp-1">{{ course.title }}</h2>
          <p class="text-sm text-ink-muted line-clamp-2 mb-4 flex-1">{{ course.description }}</p>
          <div class="flex flex-wrap gap-1.5 mb-5">
            <span v-for="(tag, idx) in (course.tags || []).slice(0, 3)" :key="idx" class="chip">{{ tag }}</span>
          </div>
          <span class="btn-primary w-full justify-center">
            {{ course.isCompleted || course.isEnrolled ? "Continue" : "Enroll Now" }}
            <i class="fas fa-arrow-right text-xs"></i>
          </span>
        </div>
      </a>
    </div>
    <div v-else class="card p-12 text-center">
      <i class="fas fa-graduation-cap text-4xl text-ink-faint mb-4"></i>
      <p class="text-ink-muted">Nothing here yet. <a href="/modules" class="font-semibold">Browse modules</a> to get started.</p>
    </div>

    <!-- Rank modal -->
    <transition
      enter-active-class="transition duration-200 ease-out"
      enter-from-class="opacity-0"
      enter-to-class="opacity-100"
      leave-active-class="transition duration-150 ease-in"
      leave-from-class="opacity-100"
      leave-to-class="opacity-0"
    >
      <div v-if="showRankModal" class="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4" @click.self="showRankModal = false">
        <div class="card w-full max-w-3xl max-h-[85vh] flex flex-col">
          <div class="flex items-center justify-between p-6 border-b border-line">
            <h2 class="text-2xl font-extrabold text-white">Rank Levels</h2>
            <button class="h-9 w-9 grid place-items-center rounded-lg text-ink-muted hover:text-white hover:bg-surface-light" @click="showRankModal = false">
              <i class="fas fa-times"></i>
            </button>
          </div>
          <div class="overflow-y-auto p-6 space-y-4">
            <div
              v-for="rankItem in rankLevels"
              :key="rankItem.name"
              :class="['flex items-center gap-5 rounded-xl border p-5', rankItem.name === rank ? 'border-brand/50 bg-brand/5' : 'border-line bg-surface-light']"
            >
              <img :src="rankItem.badgeUrl" :alt="`${rankItem.name} Badge`" class="h-16 w-16 shrink-0" />
              <div>
                <div class="flex items-center gap-2">
                  <h3 class="text-lg font-bold text-white">{{ rankItem.name }}</h3>
                  <span v-if="rankItem.name === rank" class="badge bg-brand text-white">You</span>
                </div>
                <p class="text-xs text-ink-faint mb-1">Points: {{ formatRange(rankItem.min, rankItem.max) }}</p>
                <p class="text-sm text-ink-muted">{{ rankItem.description }}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </transition>
  </AppShell>
</template>

<script>
import AppShell from "@/components/ui-components/AppShell.vue";
import apiClient from "@/axiosConfig";
import { ranks } from "@/config/ranks.js";
import { isAuthenticated } from "@/utils/auth";

export default {
  name: "DashboardPage",
  components: { AppShell },
  data() {
    return {
      username: "Hacker",
      points: 0,
      isSubscribed: false,
      rank: "Kiddie",
      nextRank: "Wannabe",
      nextRankPoints: 501,
      progressBarWidth: "0%",
      modules: [],
      tabs: [
        { id: "recommended", label: "Recommended" },
        { id: "inProgress", label: "In Progress" },
      ],
      activeTab: "recommended",
      showRankModal: false,
      rankLevels: ranks,
      isLoggedIn: false,
    };
  },
  computed: {
    filteredModules() {
      let filtered;
      if (this.activeTab === "recommended") {
        filtered = this.modules.filter((module) => !module.isCompleted).slice(0, 6);
      } else if (this.activeTab === "inProgress") {
        filtered = this.modules.filter((module) => module.isEnrolled && !module.isCompleted);
      } else {
        filtered = this.modules;
      }
      return filtered;
    },
    rankBadgeUrl() {
      const currentRank = this.rankLevels.find((rank) => rank.name === this.rank);
      return currentRank ? currentRank.badgeUrl : "/assets/badges/default.webp";
    },
    continueModule() {
      // Prefer an enrolled-but-unfinished module, else the first unfinished one
      return (
        this.modules.find((m) => m.isEnrolled && !m.isCompleted) ||
        this.modules.find((m) => !m.isCompleted) ||
        null
      );
    },
    inProgressCount() {
      return this.modules.filter((m) => m.isEnrolled && !m.isCompleted).length;
    },
    completedCount() {
      return this.modules.filter((m) => m.isCompleted).length;
    },
  },
  methods: {
    async fetchUserData() {
      if (!isAuthenticated()) {
        this.isLoggedIn = false;
        return;
      }
      try {
        const response = await apiClient.get("/users/current");
        const { user } = response.data;
        this.username = user.username;
        this.points = user.points;
        this.isSubscribed = user.isSubscribed;
        this.isLoggedIn = true;
        this.calculateRank();
      } catch (error) {
        console.error("Error fetching user data:", error);
        this.isLoggedIn = false;
      }
    },
    async fetchModules() {
      try {
        const response = await apiClient.get("/modules");
        const baseUrl = process.env.VUE_APP_BASE_URL;
        this.modules = response.data.map((module) => ({
          ...module,
          imageUrl: `${baseUrl}/static/modules/banners/${module.id}`,
        }));
      } catch (error) {
        console.error("Error fetching modules:", error);
        this.modules = [];
      }
    },
    calculateRank() {
      const currentRank = ranks.find((rank) => this.points >= rank.min && this.points <= rank.max);
      const nextRank = ranks.find((rank) => rank.min > currentRank.max);
      this.rank = currentRank.name;
      this.nextRank = nextRank ? nextRank.name : "Max Rank Achieved";
      this.nextRankPoints = nextRank ? nextRank.min - this.points : 0;
      this.progressBarWidth = `${((this.points - currentRank.min) / (currentRank.max - currentRank.min)) * 100}%`;
    },
    formatRange(min, max) {
      const formatNumber = (num) => num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
      return `${formatNumber(min)} - ${formatNumber(max)}`;
    },
    setActiveTab(tabId) {
      this.activeTab = tabId;
    },
    onImgError(e) {
      e.target.src = require("@/assets/logo.png");
    },
  },
  async mounted() {
    await this.fetchUserData();
    await this.fetchModules();
  },
};
</script>
